// ============================================================================
// components/remote/RemotePasswordConnectDialog.tsx — VIEWER-сторона входа по
// паролю (unattended, как AnyDesk): хост заранее задал пароль — подключение
// БЕЗ подтверждения на хосте. Вызов `remote.startRemoteSessionByPassword`
// (роль «Remote-клиент»/«Remote-управление»/админ; политика хоста: mode=password
// + unattended + пароль). Успех → готовые LiveKit-креды сеанса (url+token).
// Ошибки человеческие: неверный пароль, режим выключен, лимит попыток.
// ============================================================================

import { useCallback, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { logEvent } from '@/lib/eventLog';
import { errorCode, friendlyError } from '@/lib/errors';

export interface RemotePasswordCreds {
  sessionId: string;
  room: string;
  url: string;
  token: string;
}

interface Props {
  hostId: string;
  hostName: string;
  onConnected: (creds: RemotePasswordCreds) => void;
  onClose: () => void;
  /** Хост всё равно может подтвердить запрос вручную — запасной путь, если
   *  пароль неизвестен/забыт (политика consent разрешена и в режиме пароля). */
  onRequestConsent?: () => void;
}

/** Человеческий текст ошибки входа по паролю (код → сообщение). */
function passwordErrorText(e: unknown): string {
  const code = errorCode(e);
  if (code === 7 || code === 'permission_denied' || code === 'PermissionDenied') {
    return 'Неверный пароль удалённого доступа';
  }
  if (code === 9 || code === 'failed_precondition' || code === 'FailedPrecondition') {
    return 'На устройстве хоста не включён вход по паролю (нужен режим «Подключаться по паролю» и заданный пароль)';
  }
  if (code === 8 || code === 'resource_exhausted' || code === 'ResourceExhausted') {
    return 'Слишком много попыток входа по паролю — повторите через минуту';
  }
  if (code === 3 || code === 'invalid_argument' || code === 'InvalidArgument') {
    return 'Нельзя подключиться к самому себе';
  }
  if (code === 5 || code === 'not_found' || code === 'NotFound') {
    return 'Пользователь-хост не найден';
  }
  return friendlyError(e);
}

export function RemotePasswordConnectDialog({ hostId, hostName, onConnected, onClose, onRequestConsent }: Props) {
  const myName = useAuthStore((s) => s.user?.name || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = useCallback(async () => {
    if (!password) {
      setError('Введите пароль удалённого доступа');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r: { sessionId?: string; room?: string; url?: string; token?: string } =
        await getSDK().remote.startRemoteSessionByPassword({
          hostId,
          password,
          myName,
          source: 'app',
        });
      const sessionId = String(r?.sessionId || '');
      if (!sessionId) throw new Error('сервер не вернул идентификатор сеанса');
      const url = String(r?.url || '');
      const token = String(r?.token || '');
      if (!url || !token) {
        // Сеанс создан на сервере, но LiveKit не настроен — подключиться нельзя.
        // Закрываем бесполезный сеанс, ошибка — явная (без тихих полутканей).
        await getSDK().remote.endRemoteSession({ sessionId, durationSec: 0 })
          .catch((cleanupErr: unknown) => logEvent({
            level: 'warning',
            category: 'ui',
            message: 'удалённый доступ: не удалось закрыть сеанс без LiveKit',
            entityType: 'remote',
            entityCode: 'session',
            objectId: sessionId,
            details: String((cleanupErr as Error)?.message || cleanupErr),
          }));
        throw new Error('LiveKit не настроен на сервере — подключение по паролю недоступно');
      }
      void logEvent({
        level: 'info',
        category: 'ui',
        message: 'удалённый доступ: вход по паролю (unattended) выполнен',
        entityType: 'remote',
        entityCode: 'session',
        objectId: sessionId,
        details: JSON.stringify({ host_id: hostId, host_name: hostName }),
      });
      onConnected({ sessionId, room: String(r?.room || ''), url, token });
    } catch (e) {
      const msg = passwordErrorText(e);
      setError(msg);
      setPassword('');
      void logEvent({
        level: 'warning',
        category: 'ui',
        message: `удалённый доступ: вход по паролю отклонён (${hostName || hostId})`,
        entityType: 'remote',
        entityCode: 'access',
        objectId: hostId,
        details: String((e as Error)?.message || e),
      });
    } finally {
      setBusy(false);
    }
  }, [hostId, hostName, myName, password, onConnected]);

  return (
    <UniversalWindow
      open={true}
      title={`Подключение по паролю — ${hostName || hostId}`}
      onClose={onClose}
      resizable={false}
    >
      <div className="space-y-3 p-4">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Сотрудник настроил подключение по паролю (unattended) — сеанс начнётся
            сразу, без запроса подтверждения на его устройстве.
          </span>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          placeholder="Пароль удалённого доступа"
          autoComplete="off"
          autoFocus
          disabled={busy}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
        />
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          {onRequestConsent ? (
            <button
              type="button"
              onClick={onRequestConsent}
              disabled={busy}
              className="text-[11px] text-muted-foreground underline-offset-2 hover:text-primary hover:underline disabled:opacity-50"
            >
              Запросить согласие вместо пароля
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border px-3 py-1.5 text-xs disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Подключиться
            </button>
          </div>
        </div>
      </div>
    </UniversalWindow>
  );
}
